import {
  AllProjectsDocument,
  Project,
  RecentProjectsDocument,
  UpdateProjectInput,
  useUpdateProjectMutation,
} from "@gql/sites-api/graphql";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";

import { ApolloError } from "@apollo/client";
import { Icon } from "react-migration/components/Icon";
import { PROJECT_TITLE_ALREADY_EXISTS_CODE } from "../ProjectModal/constants";
import { ProjectModal } from "../ProjectModal/ProjectModal";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { useCallback } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type EditProjectModalProps = {
  project: Pick<Project, "id" | "title" | "developmentType" | "goal" | "image" | "imageUrl">;
  onClose: () => void;
};

export const EditProjectModal = ({ project, onClose }: EditProjectModalProps) => {
  const { t } = useTranslation();

  const [updateProject, { loading, error, reset }] = useUpdateProjectMutation({
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    refetchQueries: [AllProjectsDocument, RecentProjectsDocument],
  });

  const handleUpdateProject = useCallback(
    async (input: Omit<UpdateProjectInput, "id">) => {
      try {
        const { errors } = await updateProject({
          variables: {
            input: {
              id: project.id,
              ...input,
            },
          },
        });

        if (!errors?.length) {
          logEventV2({ name: "Workspace edited", properties: { projectId: project.id } });
          onClose();
        }
      } catch (error) {
        console.error("Failed to update project:", error);
      }
    },
    [updateProject, onClose, project.id]
  );

  const errorMessage = getTitleErrorMessage(error);
  const hasGenericError = !!error && !errorMessage;

  return (
    <ProjectModal
      title={<EditProjectModalTitle />}
      project={{
        name: project.title,
        developmentType: project.developmentType[0],
        goal: project.goal,
        image: project.image,
        imageUrl: project.imageUrl,
      }}
      actionLabel={t("projects.modal.update_project")}
      handleAction={handleUpdateProject}
      onClose={onClose}
      loading={loading}
      titleError={errorMessage}
      genericError={hasGenericError}
      onErrorDismiss={reset}
    />
  );
};

const EditProjectModalTitle = () => {
  const { t } = useTranslation();
  return (
    <span className="flex items-center text-lg font-semibold gap-x-2">
      <Icon icon="icon-lt-edit-line" size="lg" />
      <span>{t("projects.modal.edit_project")}</span>
    </span>
  );
};

function getTitleErrorMessage(error?: ApolloError) {
  const titleError = error?.graphQLErrors?.find(
    (error) => error.extensions?.code === PROJECT_TITLE_ALREADY_EXISTS_CODE
  );
  return titleError?.message;
}
